import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { WebsocketInit } from '@app/core/models/connection-status';
import { WebSocketService } from '@app/core/services/websocket.service';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { ButtonType } from '@app/shared/models/dialog-data';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, Subject } from 'rxjs';

/**
 * Listens for the `ConnectUCConfigSettingUpdated` websocket event, sent when an admin changes a
 * ConnectUC setting for this user/domain. Settings are NOT applied live — this only prompts the
 * user to reload so the change takes effect.
 */
@Injectable({
  providedIn: 'root',
})
export class ConfigSettingUpdatedService implements WebsocketInit {
  private readonly settingUpdatedSubject = new Subject<void>();

  private dialogRef: MatDialogRef<ConfirmDialogComponent> | null = null;

  constructor(
    private webSocketService: WebSocketService,
    private dialog: MatDialog,
    private translateService: TranslateService
  ) {
    // Collapse a burst of setting events into one prompt. Immediate feedback is not needed.
    this.settingUpdatedSubject.pipe(debounceTime(1000)).subscribe(() => this.promptReload());
  }

  public bindSocketEvents(): void {
    this.webSocketService.on('ConnectUCConfigSettingUpdated', (payload) => {
      console.log('[CONFIG] Setting updated on server, applies on reload', payload);
      this.settingUpdatedSubject.next();
    });
  }

  private promptReload(): void {
    if (this.dialogRef) {
      return;
    }

    this.dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      disableClose: true,
      data: {
        title: this.translateService.instant('CONFIG_UPDATED.TITLE'),
        icon: null,
        description: this.translateService.instant('CONFIG_UPDATED.DESCRIPTION'),
        rememberId: null,
        buttons: [
          {
            label: this.translateService.instant('CONFIG_UPDATED.LATER'),
            type: ButtonType.matButton,
            color: 'primary',
            action: () => undefined,
          },
          {
            label: this.translateService.instant('CONFIG_UPDATED.RELOAD_NOW'),
            type: ButtonType.matRaisedButton,
            color: 'primary',
            default: true,
            action: () => this.reload(),
          },
        ],
      },
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = null;
    });
  }

  /** Abstracted to allow for unit testing which cannot properly spy on `window.location` */
  protected reload(): void {
    window.location.reload();
  }
}
